import { useEffect, useRef, useState } from 'react';
import { Lightbulb, X, Send, Check } from 'lucide-react';
import { Product as C } from './productTokens';
import api from '../utils/api';

const DEFAULT_HEADING = 'What can we improve?';
const DEFAULT_PLACEHOLDER = "Tell us what's working or what's not…";

/**
 * Subtle, always-visible feedback button (bottom-right). Opens a simple
 * form that emails the admin. Never blocks the page.
 *
 * Also doubles as the real human-support channel for other pages: any page
 * can call window.dispatchEvent(new CustomEvent('trusttrade:open-feedback',
 * { detail: { prefill, heading } })) to open this already-pre-filled with
 * identifying context (e.g. a deal/milestone id) and a heading that matches
 * what's actually being asked for — see SmartDeal.jsx's "Request support
 * with this stage". Reuses this exact component/endpoint rather than a
 * second support surface, per the Final Milestone Support Safety Fix
 * (2026-10-13).
 */
export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [heading, setHeading] = useState(DEFAULT_HEADING);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous re-entrancy guard — a ref (not state) so a rapid double
  // click/Enter can't slip two requests through before React disables the
  // button (same pattern as SmartDeal's ReleaseConfirmModal).
  const sendingRef = useRef(false);

  useEffect(() => {
    const openFeedback = (event) => {
      const detail = event?.detail || {};
      if (detail.prefill) setMessage(detail.prefill);
      setHeading(detail.heading || DEFAULT_HEADING);
      setError(null);
      setOpen(true);
    };
    window.addEventListener('trusttrade:open-feedback', openFeedback);
    return () => window.removeEventListener('trusttrade:open-feedback', openFeedback);
  }, []);

  const submit = async () => {
    const msg = message.trim();
    if (!msg || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await api.post('/feedback', { message: msg, page: window.location.pathname });
      setSent(true);
      setMessage('');
      setTimeout(() => { setOpen(false); setSent(false); setHeading(DEFAULT_HEADING); }, 1600);
    } catch (e) {
      // The backend now returns a real error (with a usable fallback — the
      // actual support email address) when delivery genuinely fails,
      // instead of a misleading 200/ok:true — surface that detail verbatim
      // rather than a generic "try again" that hides the fallback.
      setError(e.response?.data?.detail || 'Could not send. Please try again or email trusttrade.register@gmail.com directly.');
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  return (
    <>
      {/* Launcher — subtle, bottom-right. Final Banking Status, Fee & Chat
          Cleanup (2026-10-14): now the only floating launcher — the AI
          SupportChat widget was removed, so this took over its former
          (already mobile-chrome-safe) vertical position instead of leaving
          an empty gap underneath. */}
      <button
        className="tt-feedback-launcher"
        onClick={() => setOpen(o => !o)}
        aria-label="Send feedback"
        title={DEFAULT_HEADING}
        data-testid="feedback-launcher-btn"
        style={{
          position: 'fixed', right: 23, bottom: 72, zIndex: 1001,
          width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
          background: C.surfaceSolid, border: `1px solid ${C.border}`, color: C.sub,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: C.shadow,
        }}
      >
        {open ? <X size={18} /> : <Lightbulb size={18} />}
      </button>

      {/* Panel */}
      {open && (
        <div data-testid="feedback-panel" style={{
          position: 'fixed', right: 23, bottom: 120, zIndex: 1001, width: 300, maxWidth: 'calc(100vw - 46px)',
          background: C.surfaceSolid, border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
          boxShadow: C.shadow, padding: 16,
        }}>
          {sent ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', color: C.success, fontSize: 14, fontWeight: 600 }}>
              <Check size={18} /> Thanks for the feedback!
            </div>
          ) : (
            <>
              <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>{heading}</p>
              <textarea
                aria-label="Your feedback"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={DEFAULT_PLACEHOLDER}
                rows={4}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 84,
                  border: `1px solid ${C.border}`, borderRadius: 10, padding: '9px 11px',
                  fontSize: 13, fontFamily: 'inherit', color: C.text, background: C.hover, outline: 'none',
                }}
              />
              {error && <p style={{ fontSize: 12, color: C.error, margin: '6px 0 0' }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button onClick={() => setOpen(false)}
                  style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 12px', fontSize: 13, color: C.sub, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submit} disabled={sending || !message.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: (sending || !message.trim()) ? C.border : C.accentGradient, border: 'none', borderRadius: 9, padding: '8px 14px',
                    fontSize: 13, fontWeight: 600, color: '#061421',
                    cursor: (sending || !message.trim()) ? 'not-allowed' : 'pointer',
                  }}>
                  <Send size={13} /> {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
